import React from 'react';
import _ from 'underscore';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Label, Icon } from '@bingads-webui-react/fabric-control';
import { HelpPopupInline } from '@bingads-webui-react/help-popup';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { getAdsStepStyles } from './styles';

const convertBpsToKbps = (bps) => {
  // Divide bps by 1,000
  const Kbps = bps / (10 ** 3);

  return Math.floor(Kbps);
};

const Placements = {
  AUDIENCE_NETWORK: _TL_('Microsoft Advertising Network'),
  GENERAL_CTV: _TL_('General CTV'),
  NETFLIX: _TL_('Premium CTV'),
};

export const PlacementQualities = {
  PERFECT: 7,
  EXCELLENT_AND_PERFECT: 6,
  PERFECT_AND_GOOD: 5,
  ONLY_PERFECT: 4,
  EXCELLENT: 3,
  ONLY_EXCELLENT: 2,
  GOOD: 1,
  NONE: 0,
};

// recently changed wording for each quality label
// perfect -> excellent
// excellent -> good
// good -> fair
export const PlacementQualityToText = {
  [PlacementQualities.PERFECT]: _TL_('Excellent'),
  [PlacementQualities.EXCELLENT]: _TL_('Good'),
  [PlacementQualities.GOOD]: _TL_('Fair'),
  [PlacementQualities.NONE]: _TL_('Poor'),
  [PlacementQualities.PERFECT_AND_GOOD]: _TL_('Excellent'),
  [PlacementQualities.EXCELLENT_AND_PERFECT]: _TL_('Excellent'),
  [PlacementQualities.ONLY_PERFECT]: _TL_('Excellent'),
  [PlacementQualities.ONLY_EXCELLENT]: _TL_('Good'),
};

const QualityToPlacements = {
  [PlacementQualities.PERFECT]: { enabled: [Placements.AUDIENCE_NETWORK, Placements.GENERAL_CTV, Placements.NETFLIX], disabled: [] },
  [PlacementQualities.EXCELLENT]: { enabled: [Placements.AUDIENCE_NETWORK, Placements.GENERAL_CTV], disabled: [Placements.NETFLIX] },
  [PlacementQualities.GOOD]: { enabled: [Placements.AUDIENCE_NETWORK], disabled: [Placements.GENERAL_CTV, Placements.NETFLIX] },
  [PlacementQualities.NONE]: { enabled: [], disabled: [Placements.AUDIENCE_NETWORK, Placements.GENERAL_CTV, Placements.NETFLIX] },
  [PlacementQualities.PERFECT_AND_GOOD]: { enabled: [Placements.AUDIENCE_NETWORK, Placements.GENERAL_CTV], disabled: [Placements.NETFLIX] },
  [PlacementQualities.EXCELLENT_AND_PERFECT]: { enabled: [Placements.GENERAL_CTV, Placements.NETFLIX], disabled: [Placements.AUDIENCE_NETWORK] },
  [PlacementQualities.ONLY_PERFECT]: { enabled: [Placements.NETFLIX], disabled: [Placements.AUDIENCE_NETWORK, Placements.GENERAL_CTV] },
  [PlacementQualities.ONLY_EXCELLENT]: { enabled: [Placements.GENERAL_CTV], disabled: [Placements.AUDIENCE_NETWORK, Placements.NETFLIX] },
};

const StarsToQuality = {
  [PlacementQualities.PERFECT]: 3,
  [PlacementQualities.EXCELLENT_AND_PERFECT]: 3,
  [PlacementQualities.PERFECT_AND_GOOD]: 3,
  [PlacementQualities.ONLY_PERFECT]: 3,
  [PlacementQualities.EXCELLENT]: 2,
  [PlacementQualities.ONLY_EXCELLENT]: 2,
  [PlacementQualities.GOOD]: 1,
  [PlacementQualities.NONE]: 0,
};

export const PlacementTitle = ({ i18n, classes, titleText }) => (
  <div style={{ display: 'flex', alignItems: 'center' }}>
    <Label className={classes.placementLabel}> {i18n.getString(titleText)} </Label>
    <HelpPopupInline topic="pop_BA_VideoAds_EligibleNetworks" />
  </div>
);

PlacementTitle.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  classes: PropTypes.shape({
    placementLabel: PropTypes.string.isRequired,
  }).isRequired,
  titleText: PropTypes.string.isRequired,
};


export const PlacementInfo = (props) => {
  const {
    i18n,
    videoAd,
  } = props;

  const defaultClasses = useDefaultStyles(getAdsStepStyles);

  const classes = defaultClasses;

  if (videoAd && videoAd.video) {
    const {
      width, height, quality = 0, sourceVideoBitRate,
    } = videoAd.video;

    return (
      <div className={classes.placementInfoTile}>
        <PlacementTitle i18n={i18n} classes={classes} titleText={_TL_('Eligible Networks')} />
        <div style={{ fontSize: '12px' }} className={classes.placmentResolution}> {width}px x {height}px, {`${convertBpsToKbps(sourceVideoBitRate)} ${_TL_('Kbps')}`} ({PlacementQualityToText[quality]} {i18n.getString(_TL_('quality'))})</div>
        <div className={classes.placementInfos}>
          {QualityToPlacements[quality].enabled.map(placement => (
            <div className={classes.enabledPlacement}> <span className={classes.enabledText}> <Icon className={classes.fontStyle} iconName="CheckMark" /> {i18n.getString(placement)} </span> </div>
          ))}
          {QualityToPlacements[quality].disabled.map(placement => (
            <div className={classes.disabledPlacement}> <span className={classes.disabledText}> <Icon className={classes.fontStyle} iconName="Blocked2" />{i18n.getString(placement)} </span> </div>
          ))}
        </div>
      </div>
    );
  }

  return null;
};

PlacementInfo.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  videoAd: PropTypes.shape({
    video: PropTypes.shape({
      width: PropTypes.number,
      height: PropTypes.number,
      quality: PropTypes.number,
      sourceVideoBitRate: PropTypes.number,
    }),
  }).isRequired,
};


export const PlacementInfoWithStars = (props) => {
  const {
    i18n,
    videoAd,
  } = props;

  const defaultClasses = useDefaultStyles(getAdsStepStyles);

  const classes = defaultClasses;

  if (videoAd && videoAd.video) {
    const {
      width, height, quality = 0, sourceVideoBitRate,
    } = videoAd.video;

    const numberOfStars = StarsToQuality[quality] || 0;

    const numberOfDisabledStars = 3 - numberOfStars;

    return (
      <div className={classes.placementInfoTile}>
        <PlacementTitle i18n={i18n} classes={classes} titleText={_TL_('Quality')} />
        <div className={classes.placementInfos}>
          {
            _.range(numberOfStars).map(idx => (
              <Icon key={`${idx}-enabled`} className={classNames(['enabled-star', classes.starStyle])} iconName="FavoriteStarFill" />
            ))
          }
          {
            numberOfDisabledStars > 0 && _.range(numberOfDisabledStars).map(idx => (
              <Icon key={`${idx}-disabled`} className={classNames(['disabled-star', classes.starStyle])} iconName="FavoriteStar" />
            ))
          }
          ({PlacementQualityToText[quality]} {i18n.getString(_TL_('quality'))})
        </div>
        <div style={{ fontSize: '12px' }} className={classes.placmentResolution}> {width}px x {height}px, {`${convertBpsToKbps(sourceVideoBitRate)} ${_TL_('Kbps')}`} </div>
      </div>
    );
  }

  return null;
};


PlacementInfoWithStars.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  videoAd: PropTypes.shape({
    video: PropTypes.shape({
      width: PropTypes.number,
      height: PropTypes.number,
      quality: PropTypes.number,
      sourceVideoBitRate: PropTypes.number,
    }),
  }).isRequired,
};
